import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { TranslateModule } from '@ngx-translate/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { ImageCropperModule } from 'ngx-image-cropper';
import {
  InfiniteScrollDirective,
  InfiniteScrollModule,
} from 'ngx-infinite-scroll';
import {
  IConfig,
  NgxMaskDirective,
  NgxMaskPipe,
  provideNgxMask,
} from 'ngx-mask';
import {
  AccountRoleComponent,
  AddContainerComponent,
  AdditionalFilesViewComponent,
  DateTimePickerComponent,
  DocumentAdditionalComponent,
  EntryAdditionalComponent,
  FlowRejectModalComponent,
  FooterComponent,
  HeaderComponent,
  LoadUnloadAdditionalComponent,
  LoadingAdditionalComponent,
  OnLoadingAdditionalComponent,
  PageNotFoundComponent,
  PrivacyPolicyComponent,
  ProfileCompanyComponent,
  ReferenceBoxComponent,
  RegulatoryDetailComponent,
  RegulatoryDocumentViewerComponentShared,
  RejectedInfoComponent,
  SidebarComponent,
  SubmenuComponent,
  SubmissionDetailComponentShared,
  VesselLocodeComponent,
  WeighbridgeAdditionalComponent,
  WrapDetailComponent,
  CategoryTabComponent,
} from './components';
import { AvatarUploadComponent } from './components/avatar-upload/avatar-upload.component';
import { CancelledActionComponent } from './components/cancelled-action/cancelled-action.component';
import { DiscussionBoxComponent } from './components/discussion-box/discussion-box.component';
import { DynamicFormBuilderComponent } from './components/dynamic-form-builder/dynamic-form-builder.component';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { AdminFilterComponent } from './components/filter-boxes/admin-filter/admin-filter.component';
import { IntermodalFilterComponent } from './components/filter-boxes/intermodal-filter/intermodal-filter.component';
import { FlowJourneyPanelComponent } from './components/flow-journey-panel/flow-journey-panel.component';
import { FlowPanelComponent } from './components/flow-panel/flow-panel.component';
import { FormsPanelComponent } from './components/forms-panel/forms-panel.component';
import { GroupsPanelComponent } from './components/groups-panel/groups-panel.component';
import { InputFileComponent } from './components/input-file/input-file.component';
import { NotificationComponent } from './components/notification/notification.component';
import { PanelBoxComponent } from './components/panel-box/panel-box.component';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { StepperComponent } from './components/stepper/stepper.component';
import {
  ClickOutsideDirective,
  DndDirective,
  MaxLengthInputDirective,
  OnlyNumberDirective,
  SearchDirective,
  FieldErrorsDirective,
} from './directives';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { CompanyTabComponent } from './layouts/company-tab/company-tab.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { NavTabBarComponent } from './layouts/nav-tab-bar/nav-tab-bar.component';
import {
  CheckJourneyPointPipe,
  DateUTCPipe,
  FilterPipe,
  HidePhonePipe,
  JoinPipe,
  PersonaIconPipe,
  PluralizePipe,
  StatusPipe,
} from './pipes';
import { SafeURLPipe } from './pipes/safeURL.pipe';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReplacePipe } from './pipes/replace.pipe';

const MATERIAL_MODULE = [
  MatButtonModule,
  MatInputModule,
  MatIconModule,
  MatToolbarModule,
  MatFormFieldModule,
  MatTableModule,
  MatPaginatorModule,
  MatTooltipModule,
  MatSortModule,
  MatDividerModule,
  MatExpansionModule,
  MatSliderModule,
  MatSlideToggleModule,
  MatCheckboxModule,
  MatCardModule,
  DragDropModule,
  MatStepperModule,
  CdkStepperModule,
  MatDialogModule,
  PdfViewerModule,
  MatSelectModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatProgressSpinnerModule,
];

const SHARED_PIPES = [
  FilterPipe,
  PluralizePipe,
  StatusPipe,
  SafeURLPipe,
  JoinPipe,
  HidePhonePipe,
  PersonaIconPipe,
  CheckJourneyPointPipe,
  DateUTCPipe,
  ReplacePipe,
];

const SHARED_DIRECTIVES = [
  OnlyNumberDirective,
  ClickOutsideDirective,
  DndDirective,
  SearchDirective,
  MaxLengthInputDirective,
  FieldErrorsDirective,
];

const SHARED_COMPONENTS = [
  PageNotFoundComponent,
  FooterComponent,
  HeaderComponent,
  SidebarComponent,
  SubmenuComponent,
  DiscussionBoxComponent,
  InputFileComponent,
  AvatarUploadComponent,
  ProfileCompanyComponent,
  IntermodalFilterComponent,
  AdminFilterComponent,
  PanelBoxComponent,
  FlowPanelComponent,
  FormsPanelComponent,
  GroupsPanelComponent,
  ReferenceBoxComponent,
  DynamicFormBuilderComponent,
  DynamicFormComponent,
  PdfViewerComponent,
  FlowJourneyPanelComponent,
  NotificationComponent,
  RegulatoryDetailComponent,
  SubmissionDetailComponentShared,
  RegulatoryDocumentViewerComponentShared,
  CancelledActionComponent,
  PrivacyPolicyComponent,
  DocumentAdditionalComponent,
  LoadUnloadAdditionalComponent,
  OnLoadingAdditionalComponent,
  LoadingAdditionalComponent,
  AdditionalFilesViewComponent,
  AccountRoleComponent,
  WeighbridgeAdditionalComponent,
  EntryAdditionalComponent,
  WrapDetailComponent,
  FlowRejectModalComponent,
  RejectedInfoComponent,
  VesselLocodeComponent,
  AddContainerComponent,
  CategoryTabComponent,
];

const LAYOUT_COMPONENTS = [
  AuthLayoutComponent,
  MainLayoutComponent,
  AdminLayoutComponent,
  StepperComponent,
  NavTabBarComponent,
  CompanyTabComponent,
];

const maskConfigFunction: () => Partial<IConfig> = () => {
  return {
    thousandSeparator: ',',
  };
};
@NgModule({
  declarations: [
    ...SHARED_PIPES,
    ...SHARED_COMPONENTS,
    ...LAYOUT_COMPONENTS,
    ...SHARED_DIRECTIVES,
  ],
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    RouterModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ImageCropperModule,
    LoadingBarModule,
    TranslateModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    DateTimePickerComponent,
    InfiniteScrollModule,
    ...MATERIAL_MODULE,
  ],
  exports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    RouterModule,
    NgxMaskDirective,
    NgxMaskPipe,
    TranslateModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    InfiniteScrollDirective,
    ...MATERIAL_MODULE,
    ...SHARED_PIPES,
    ...SHARED_COMPONENTS,
    ...LAYOUT_COMPONENTS,
    ...SHARED_DIRECTIVES,
  ],
  providers: [provideNgxMask(maskConfigFunction)],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
